import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button, Alert } from 'antd';
import { MailOutlined, LockOutlined, FieldTimeOutlined, ProjectOutlined, SunOutlined } from '@ant-design/icons';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './LoginPage.css';

interface LoginFormData {
  email: string;
  password: string;
}

type LoginError = { message: string; hint?: string };

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);

  const onFinish = async (values: LoginFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/login`, values);
      const { token } = response.data;

      if (response.data.user.role === 'admin') {
        localStorage.setItem('token', token);
        toast.success(t('auth.loginSuccess'));
        // Full reload on purpose: every service reads the token when its module loads
        window.location.href = '/dashboard';
        return;
      }

      // The login itself worked, so the backend issued a token we won't use — revoke it.
      axios.get(`${API_URL}/logout`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      setError({ message: t('auth.adminAccessOnly'), hint: t('auth.employeesUseApp') });
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401 || status === 422) {
        setError({ message: t('auth.invalidCredentials') });
      } else if (axios.isAxiosError(err) && !err.response) {
        setError({ message: t('auth.networkError') });
      } else {
        setError({ message: t('auth.loginFailed') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const points = [
    { icon: <FieldTimeOutlined />, text: t('auth.brandPointAttendance') },
    { icon: <ProjectOutlined />, text: t('auth.brandPointProjects') },
    { icon: <SunOutlined />, text: t('auth.brandPointLeave') },
  ];

  return (
    <div className="lgn">
      <aside className="lgn-brand" aria-hidden>
        <div className="lgn-brand-logo">
          <img src="/estethica-logo.png" alt="" />
        </div>
        <div className="lgn-brand-body">
          <h2 className="lgn-brand-title">{t('auth.brandTitle')}</h2>
          <ul className="lgn-brand-points">
            {points.map((point) => (
              <li key={point.text}>
                <span className="lgn-brand-icon">{point.icon}</span>
                {point.text}
              </li>
            ))}
          </ul>
        </div>
        <div className="lgn-brand-footer">© {new Date().getFullYear()} Estethica22</div>
      </aside>

      <main className="lgn-main">
        <div className="lgn-top">
          <LanguageSwitcher />
        </div>

        <div className="lgn-panel">
          <img className="lgn-logo" src="/estethica-logo.png" alt="Estethica22" />
          <h1 className="lgn-title">{t('auth.welcomeBack')}</h1>
          <p className="lgn-subtitle">{t('auth.signInSubtitle')}</p>

          {error ? (
            <Alert
              className="lgn-alert"
              type="error"
              showIcon
              message={error.message}
              description={error.hint}
            />
          ) : null}

          <Form
            name="login"
            layout="vertical"
            size="large"
            requiredMark={false}
            onFinish={onFinish}
            onValuesChange={() => error && setError(null)}
          >
            <Form.Item
              name="email"
              label={t('auth.email')}
              rules={[
                { required: true, message: t('auth.emailRequired') },
                { type: 'email', message: t('auth.emailInvalid') },
              ]}
            >
              <Input
                prefix={<MailOutlined className="lgn-input-icon" />}
                type="email"
                autoComplete="username"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('auth.password')}
              rules={[{ required: true, message: t('auth.passwordRequired') }]}
            >
              <Input.Password
                prefix={<LockOutlined className="lgn-input-icon" />}
                autoComplete="current-password"
              />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={submitting} className="lgn-submit">
              {t('auth.loginButton')}
            </Button>
          </Form>
        </div>

        <footer className="lgn-footer">
          <Link to="/privacy-policy">{t('auth.privacyPolicy')}</Link>
        </footer>
      </main>
    </div>
  );
};

export default LoginPage;
